import { useContext } from 'react';

import Image from 'next/image';

import { defaultCreditCost } from '../constants/credits';
import CreditContext from '../context/credits/context';

import { Button } from './Button';
import PremiumIcon from './PremiumIcon';

interface Props {
  buttonText: string;
  creditCost?: number;
  disabled: boolean;
  onClick: () => void;
  isPremium?: boolean;
  isMixedCredits?: boolean;
  regularCredits?: number;
  premiumCredits?: number;
}

export default function GenerateButton(props: Props) {
  const { context: creditContext } = useContext(CreditContext);
  const { isLoading: isCreditLoading } = creditContext;
  const {
    buttonText,
    creditCost = defaultCreditCost,
    disabled,
    onClick,
    isPremium = false,
    isMixedCredits = false,
    regularCredits,
    premiumCredits,
  } = props;

  const premiumIcon = <PremiumIcon className="h-3 fill-[#FFB743]" />;
  const regularIcon = (
    <Image
      alt="stars"
      className="mr-1 sm:ml-2"
      height={10}
      src="/images/blue-stars.svg"
      width={10}
    />
  );

  const creditsIcon = isPremium ? premiumIcon : regularIcon;
  const creditsText = isPremium ? 'Premium' : 'Regular';
  const loadingText = 'Loading...';

  return (
    <>
      <Button
        className="inline-block rounded border border-blue-600 bg-blue-600 px-5 py-3 text-sm font-medium text-white hover:bg-blue-500 focus:outline-none active:bg-blue-600 disabled:cursor-not-allowed disabled:bg-blue-600 disabled:text-white disabled:opacity-60 disabled:hover:border-blue-600"
        disabled={disabled || isCreditLoading}
        type="submit"
        onClick={onClick}
      >
        <span className="flex flex-col overflow-hidden">
          <span className="flex items-center justify-center">
            <span className="whitespace-nowrap">
              {isCreditLoading ? loadingText : buttonText}
            </span>
          </span>
        </span>
      </Button>
      <hr className="mt-2 mb-1 block w-full border-t border-white opacity-50" />
      <span className="flex items-center justify-center text-sm">
        <span className="whitespace-nowrap">This will use</span>
        {isMixedCredits ? (
          <>
            {regularCredits > 0 && (
              <>
                {regularIcon}
                <span className="whitespace-nowrap">
                  {`${regularCredits} Regular Credit${
                    regularCredits > 1 ? 's' : ''
                  }`}
                </span>
              </>
            )}
            {regularCredits > 0 && premiumCredits > 0 && (
              <span className="ml-2 inline">and </span>
            )}
            {premiumCredits > 0 && (
              <>
                {premiumIcon}
                <span className="whitespace-nowrap">
                  {`${premiumCredits} Premium Credit${
                    premiumCredits > 1 ? 's' : ''
                  }`}
                </span>
              </>
            )}
          </>
        ) : (
          <>
            {creditsIcon}
            <span className="whitespace-nowrap">
              {`${creditCost} ${creditsText} Credit${
                creditCost > 1 ? 's' : ''
              }`}
            </span>
          </>
        )}
      </span>
    </>
  );
}
